import { differenceInCalendarDays, getDay, isSameDay, startOfDay } from "date-fns"
import * as Effects from "redux-saga/effects"
import { EventStatus, IEvent } from "../../models/Event"
import { CleaningStatus, FeedingType } from "../../models/Object"
import { EventActions, RoomActions } from "../../reducers/bookingCalendar/actionNames"
import { getCalendarId } from "../../reducers/bookingCalendar/selectors/calendar"
import { withoutCancelled } from "../../reducers/bookingCalendar/selectors/events"
import { TaskActions } from "../../reducers/tasks/actionNames"
import { getTaskDate } from "../../reducers/tasks/selectors"
import {
  getSettingsBedding,
  getSettingsCleaning,
  getSettingsTowels
} from "../../reducers/userObject/selectors/settings"

const NotUnavaiable = e => e.stage !== EventStatus.Unavailable

function getFeeding(events: IEvent[], date) {
  const breakfast = { adults: 0, kids: 0 }
  const dinner = { adults: 0, kids: 0 }
  const supper = { adults: 0, kids: 0 }

  events.forEach(e => {

    switch (e.service) {
      case FeedingType.Breakfast:
        if (!isSameDay(e.startDate, date)) {
          breakfast.adults += e.adults
          breakfast.kids += e.kids
        }
        break
      case FeedingType.FullFood:
        if (!isSameDay(e.startDate, date)) {
          breakfast.adults += e.adults
          breakfast.kids += e.kids
        }
        if (!isSameDay(e.finishDate, date)) {
          supper.adults += e.adults
          supper.kids += e.kids
          dinner.kids += e.kids
          dinner.adults += e.adults
        }
        break
      case FeedingType.WithDinner:
        if (!isSameDay(e.startDate, date)) {
          breakfast.adults += e.adults
          breakfast.kids += e.kids
        }
        if (!isSameDay(e.finishDate, date)) {
          dinner.adults += e.adults
          dinner.kids += e.kids
        }
        break
    }
  })

  return { breakfast, dinner, supper }
}

function getCheckOutTasks(events: IEvent[], date: Date) {
  return events.filter(
    e => {
      return isSameDay(e.finishDate, date) && e.registration === '2'
    },
  )
}

function getByDayTasks(events: IEvent[], date: Date, days: number) {
  return events.filter(e => {
    if (isSameDay(e.finishDate, date) && e.registration !== '2') {
      return false
    }
    const diff = differenceInCalendarDays(date, e.startDate)
    return diff ? !(diff % days) : 0
  })
}

function getByWeekDaysTasks(events: IEvent[], date: Date, weekDays: boolean[]) {
  return events.filter(e => {
    if (
      (isSameDay(e.finishDate, date) &&
        weekDays[getDay(e.finishDate)] &&
        e.registration !== '2') ||
      (isSameDay(e.startDate, date) && weekDays[getDay(e.startDate)])
    ) {
      return false
    }
    return weekDays[getDay(date)]
  })
}

// eslint-disable-next-line
function* getServices(services: object, events: IEvent[], date: Date) {
  const obj = {}
  Object.entries(services).forEach(([key, value]) => {
    if (value.on) {
      switch (value.status) {
        case CleaningStatus.Days:
          if (!value.days) {
            return
          }
          obj[key] = getByDayTasks(events, date, value.days)
          break
        case CleaningStatus.CheckOut:
          obj[key] = getCheckOutTasks(events, date)
          break
        case CleaningStatus.WeekDays:
          obj[key] = getByWeekDaysTasks(events, date, value.weekDays)
          break
      }
    }
  })
  return obj
}

function loadFromDb() {
  let events, ending, starting, noPayment
  return function*(): any {
    const date = yield Effects.select(getTaskDate)
    const nDate = startOfDay(date)
    const reduxEvents = yield Effects.select(withoutCancelled)

    const withoutUnavailable = reduxEvents.filter(NotUnavaiable)

    events = reduxEvents.filter(
      e => +e.startDate <= +nDate && +e.finishDate >= +nDate,
    )

    ending = withoutUnavailable.filter(e => {
      return +e.finishDate === +nDate
    })

    starting = withoutUnavailable.filter(e => +e.startDate === +nDate)

    noPayment = withoutUnavailable
      .filter(e => e.stage === EventStatus.NoPayment)
      .filter(e => +e.finishDate >= +nDate)
    return { ending, events, noPayment, starting }
  }
}

function* loadTask(action, eventsGenerator) {
  const calendarId = yield Effects.select(getCalendarId)

  if (!calendarId) {
    yield Effects.delay(500)
    yield Effects.put({ type: TaskActions.LoadTask })
    return
  }

  const { events, ending, starting, noPayment } = yield* eventsGenerator()

  const date = yield Effects.select(getTaskDate)

  const services = yield Effects.all({
    bedding: Effects.select(getSettingsBedding),
    cleaning: Effects.select(getSettingsCleaning),
    towels: Effects.select(getSettingsTowels),
  })


  const feedingEvents = events.filter(
    e =>
      e.service !== FeedingType.WithoutFood &&
      e.stage !== EventStatus.Canceled &&
      !e.isDeleted,
  )

  const cEvents = events.filter(
    e =>
      e.stage !== EventStatus.Canceled &&
      e.stage !== EventStatus.Unavailable &&
      !e.isDeleted,
  )

  const feedingTask = getFeeding(feedingEvents, date)

  const serviceEvents = yield* getServices(services, cEvents, date)

  const serviceTask = []

  Object.entries(serviceEvents).forEach(([key, val]: any) => {
    val.forEach(e => {
      const index = serviceTask.findIndex(ev => String(ev.id) === String(e.id))
      if (index === -1) {
        serviceTask.push({ ...e, [key]: true, tasks: `${key}` })
      } else {
        serviceTask[index] = {
          ...serviceTask[index],
          [key]: true,
          tasks: `${serviceTask[index].tasks}, ${key}`,
        }
      }
    })
  })

  yield Effects.put({
    cleaning: serviceTask,
    ending,
    feeding: feedingTask,
    noPayment: noPayment,
    starting,
    type: TaskActions.TaskAccepted,
  })
}

export default function*() {
  const eventsGenerator = loadFromDb()
  yield Effects.debounce(
    200,
    [
      TaskActions.LoadTask,
      EventActions.EventWasUpdated,
      EventActions.DataForEventsAccepted,
      EventActions.EventWasDeleted,
      RoomActions.RoomWasUpdated,
      RoomActions.DataForRoomsAccepted,
    ],
    function*(action) {
      yield* loadTask(action, eventsGenerator)
    },
  )
}
